<!-- @format -->
<template>
    <div class="end-call-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <div class="widget-title">
                    <g3-end-call-fill-svg />
                    <h3>{{ callElapsedTime }} Call ended</h3>
                </div>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-svg v-if="loading" />
                <div v-else>
                    <div>
                        <div class="call-summary">
                            <div class="call-summary-title">Call with:</div>
                            <div class="call-recipient">
                                <app-profile-image
                                    class="call-recipient-image"
                                    :src="getProfileImageSrc(callRecipient.profileImageUrl)"
                                />
                                <div>
                                    <div class="call-recipient-name">{{ callRecipient.name }}</div>
                                    <div class="call-recipient-phone">
                                        {{ callRecipient.phone.number }} ({{
                                            callRecipient.phone.type
                                        }})
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="recordingUrl">
                            <div class="section-title recording">Recording</div>
                            <app-audio-player :src="recordingUrl" @loaded="updateCallElapsedTime" />
                        </div>
                        <div>
                            <div class="section-title call-outcome">Call outcome</div>
                            <v-autocomplete
                                class="app-select condensed"
                                outlined
                                rounded
                                single-line
                                :value="callOutcome"
                                :items="options"
                                @input="emitInput"
                            />
                        </div>
                        <div class="tasks">
                            <div class="outlined-section">
                                <div class="section-title">Follow-Up Tasks</div>
                                <div v-if="tasks.length > 0">
                                    <div
                                        v-for="task in tasks"
                                        :key="task.id"
                                        class="tasks-container"
                                    >
                                        <div class="task-grid">
                                            <div class="task-avatar-wrapper">
                                                <div class="task-avatar-cell">
                                                    <div
                                                        class="task-avatar"
                                                        :style="{
                                                            backgroundImage: `url('${getProfileImageSrc(callRecipient.profileImageUrl)}')`,
                                                        }"
                                                    >
                                                        <img
                                                            class="task-icon"
                                                            :src="getTaskType(task.type)"
                                                        />
                                                    </div>
                                                </div>
                                                <div class="task-type">
                                                    Follow Up {{ task.type }}
                                                </div>
                                            </div>

                                            <div class="task-text">
                                                <div class="task-title">{{ task.title }}</div>
                                            </div>

                                            <div class="task-date">
                                                {{ task.dueDate | momentWeekDay | UpperCaseString }}
                                                |
                                                {{
                                                    task.dueDate
                                                        | momentDateMMMDYYYY
                                                        | UpperCaseString
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <app-button class="add-task-button" @click="openTaskWidget">
                                    Add Follow Up Task <o6-add-svg color="#fff" />
                                </app-button>
                            </div>
                        </div>
                        <div class="call-notes">
                            <div class="section-title">Call notes</div>
                            <froala v-model="callNotes" :config="config" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-button" @click="closeWidget">Close</app-button>
                </div>
                <div class="action-container">
                    <app-button @click="save()">Save</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppAudioPlayer = () => import('@/components/common/app-audio-player');
const AppButton = () => import('@/components/common/app-button');
const AppProfileImage = () => import('@/components/common/app-profile-image');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');
const G3EndCallFillSvg = () => import('@/components/svg/grid/g3-end-call-fill-svg');
const O6AddSvg = () => import('@/components/svg/grid/o6-add-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');

import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'end-call-widget',

    components: {
        'app-button': AppButton,
        'app-profile-image': AppProfileImage,
        'loading-svg': LoadingSvg,
        'app-audio-player': AppAudioPlayer,
        'g3-end-call-fill-svg': G3EndCallFillSvg,
        'o6-add-svg': O6AddSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        options: {
            type: Object,
        },

        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            contactId: this.widget.contactId,
            loading: true,
            recordingUrl: 'https://samplelib.com/lib/preview/mp3/sample-15s.mp3', // TODO KYM: placeholder for recording URL
            callElapsedTime: '00:00', // TODO KYM: placeholder for call elapsed time

            callRecipient: null,

            options: ['Completed', 'No Answer', 'Voicemail', 'Busy', 'Wrong Number'],
            callOutcome: this.widget.callOutcome || 'No Answer',
            callNotes: '',

            tasks: [
                {
                    id: 1,
                    dueDate: this.$moment('2023-10-15'),
                    title: 'Need to finish meeting about our agreement. Hopefully we can agree on a decision soon.',
                    type: 'Call',
                    color: 'red-accent',
                },
                {
                    id: 2,
                    dueDate: this.$moment('2025-01-30'),
                    title: 'Summarize the key points from the call and send to the team.',
                    type: 'Email',
                    color: 'blue-accent',
                },
                {
                    id: 3,
                    dueDate: this.$moment('2025-06-30'),
                    title: 'Send follow-up email with meeting summary, next steps, and action items discussed. Include deadlines and responsible parties for each item.',
                    type: 'Note',
                    color: 'blue-accent',
                },
            ],
        };
    },

    computed: {
        config() {
            return {
                key: this.$helpers.getFroalaKey(),
                imageUploadParam: 'file',
                fileUploadParam: 'file',

                imageUploadURL: 'https://api.honeycrm.com/api/Files/Froala',
                fileUploadURL: 'https://api.honeycrm.com/api/Files/Froala',

                imageUploadMethod: 'POST',
                fileUploadMethod: 'POST',

                imageMaxSize: 4 * 1024 * 1024,
                fileMaxSize: 4 * 1024 * 1024,

                imageAllowedTypes: ['jpeg', 'jpg', 'png'],
                fileAllowedTypes: ['*'],
                requestHeaders: {
                    Authorization: 'Bearer ' + localStorage.jwt,
                },

                placeholderText: 'Type Away...',
                toolbarButtons: ['bold', 'italic', 'insertLink', 'align', 'formatUL'],
            };
        },
    },

    created() {
        this.loading = false;
        this.getContact();
    },

    methods: {
        getContact() {
            this.isLoading = true;
            return new Promise((resolve, reject) => {
                var restUrl = `/service/Contacts?$filter=Id eq ${this.contactId}`;
                this.$api
                    .get(restUrl)
                    .then((response) => {
                        this.callRecipient = this.mapContact(response.data.value[0]);
                        resolve();
                    })
                    .catch(reject)
                    .finally(() => {
                        this.isLoading = false;
                    });
            });
        },

        mapContact(contact) {
            return {
                name: this.$helpers.getName(contact.FirstName, contact.LastName),
                email: contact.Email,
                profileImageUrl: contact.ProfileImageUrl || require('@/assets/images/avatar.png'),
                phone: { number: contact.Phone, type: 'Mobile Phone' },
            };
        },

        getProfileImageSrc(url) {
            return url || url !== null ? url : require('@/assets/images/avatar.png');
        },

        getTaskType(type) {
            switch (type) {
                case 'Call':
                    return require('@/assets/images/phone-stroke-icon.png');
                case 'Email':
                    return require('@/assets/images/email-stroke-icon.png');
                case 'Note':
                    return require('@/assets/images/note-stroke-icon.png');
                case 'Meeting':
                    return require('@/assets/images/meeting-stroke-icon.png');
                default:
                    return '';
            }
        },

        updateCallElapsedTime(durationInSeconds) {
            if (!durationInSeconds || isNaN(durationInSeconds)) {
                this.callElapsedTime = '00:00';
                return;
            }

            const minutes = Math.floor(durationInSeconds / 60)
                .toString()
                .padStart(2, '0');
            const seconds = Math.floor(durationInSeconds % 60)
                .toString()
                .padStart(2, '0');

            this.callElapsedTime = `${minutes}:${seconds}`;
        },

        openTaskWidget() {
            EventBus.$emit('open-widget', {
                name: 'quick-add-task-widget',
                options: {
                    userId: '50e3cb88-b976-4f8c-8b7d-c678ed253549', // TODO KYM: replace with user id
                    contactId: 3190985, // TODO KYM: replace with contact id,
                },
            });
        },

        emitInput(item) {
            this.$emit('input', item);
            this.$refs.autocomplete.blur();
        },

        showAlertPopup() {
            const confirmFunction = () => {
                EventBus.$emit('close-widget', this.widget);
            };

            const actions = {
                confirm: {
                    label: 'Close',
                    action: confirmFunction,
                },

                cancel: {
                    label: 'Go Back',
                },
            };

            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Close without saving?',
                    actions,
                    content:
                        '<p>The call has been logged, but the call outcome, and all the notes you took during and after the call haven’t been saved yet. You can close without saving, or cancel and save your notes.</p>',
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        save() {
            // TODO KYM: Implement save logic
            EventBus.$emit('close-widget', this.widget);
        },

        closeWidget() {
            this.showAlertPopup();
        },
    },
};
</script>

<style>
.end-call-widget .call-summary,
.end-call-widget .call-recipient,
.end-call-widget .widget-title {
    display: flex;
    align-items: center;
}

.end-call-widget .widget-title .g3-end-call-fill-svg {
    margin-right: 8px;
}

.end-call-widget.widget .content-wrapper {
    padding: 24px;
}

.end-call-widget .section-title {
    font-weight: 500;
    font-size: 16px;
    line-height: 16px;
    margin: 32px 0 8px 0;
}

.end-call-widget .o6-add-svg {
    margin-left: 8px;
}

.end-call-widget .call-summary-title {
    font-weight: 800;
    font-size: 20px;
    margin-right: 10px;
}

.end-call-widget .call-recipient-image {
    margin-right: 16px;
}

.end-call-widget .call-recipient .call-recipient-name {
    font-weight: 700;
    font-family: 'Nunito';
    font-size: 20px;
    line-height: 25px;
}

.end-call-widget .call-recipient .call-recipient-phone {
    font-weight: 400;
    font-size: 12px;
    line-height: 12px;
}

.end-call-widget .outlined-section {
    position: relative;
    border: 1px solid #cac6cb;
    border-radius: 16px;
    padding: 24px;
}

.end-call-widget .outlined-section .add-task-button {
    width: 100%;
}

.end-call-widget .outlined-section:not(:last-child) {
    margin-bottom: 40px;
}

.end-call-widget .outlined-section .section-title {
    font: normal normal bold 16px Roboto;
    position: absolute;
    top: -10px;
    left: 0;
    right: 0;
    margin: auto;
    width: fit-content;
    padding: 0px 25px;
    background-color: #ffffff;
    font-weight: 600;
}

.end-call-widget .outlined-section .content-container {
    font-size: 16px;
}

.end-call-widget .widget-footer .cancel-button {
    border: 1px solid var(--color-border-button-cancel);
}

.end-call-widget .v-text-field__details {
    display: none;
}

.end-call-widget .v-input__slot {
    margin-bottom: 48px;
}

.end-call-widget .tasks {
    margin-top: 24px;
}

.end-call-widget .tasks-container {
    margin-bottom: 24px;
}

.end-call-widget .tasks-container:not(:last-child) {
    border-bottom: 1px solid var(--color-border-accent-silvergray);
    padding-bottom: 24px;
}

.end-call-widget .task-grid {
    display: grid;
    grid-template-columns: 1fr 1.5fr 1fr;
    gap: 12px;
    align-items: center;
}

.end-call-widget .task-avatar-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-start;
}

.end-call-widget .task-avatar-cell {
    display: flex;
    align-items: center;
    justify-content: center;
}

.end-call-widget .task-avatar {
    width: 50px;
    height: 50px;
    background-size: cover;
    background-position: center;
    border-radius: 50%;
    position: relative;
    margin-right: 16px;
}

.end-call-widget .task-icon {
    position: absolute;
    bottom: -2px;
    right: -2px;
    width: 16px;
    height: 16px;
}

.end-call-widget .task-text {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    font-size: 14px;
    color: #2e1c48;
}

.end-call-widget .task-type {
    font-size: 12px;
    line-height: 16px;
    width: 64px;
}

.end-call-widget .task-title {
    font-size: 12px;
    line-height: 16px;
}

.end-call-widget .task-date {
    display: flex;
    justify-content: flex-end;
    font-size: 16px;
    line-height: 16px;
}
</style>
